import { Component, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { getPasswordChecks, getPasswordStrength, passwordsMatchValidator, passwordStrengthValidator } from '../password.validators';

@Component({
  selector: 'app-first-login-password',
  standalone: false,
  templateUrl: './first-login-password.component.html',
  // Same visual as the forgot-password "create new password" step
  styleUrl: '../reset-password/reset-password.component.scss',
})
export class FirstLoginPasswordComponent {

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private authService = inject(AuthService);

  isSubmitting = signal(false);
  errorMessage = signal('');
  submitted = signal(false);
  showPassword = signal(false);
  showConfirmPassword = signal(false);

  passwordForm = this.fb.group(
    {
      password: this.fb.nonNullable.control('', [
        Validators.required,
        Validators.minLength(8),
        passwordStrengthValidator(),
      ]),
      confirmPassword: this.fb.nonNullable.control('', [Validators.required]),
    },
    { validators: [passwordsMatchValidator] }
  );

  get passwordControl(): FormControl<string> {
    return this.passwordForm.controls.password;
  }

  get confirmPasswordControl(): FormControl<string> {
    return this.passwordForm.controls.confirmPassword;
  }

  get passwordChecks() {
    return getPasswordChecks(this.passwordControl.value);
  }

  get passwordStrength() {
    return getPasswordStrength(this.passwordControl.value);
  }

  get canSubmitPassword(): boolean {
    return this.passwordForm.valid;
  }

  togglePassword() {
    this.showPassword.update(v => !v);
  }

  toggleConfirmPassword() {
    this.showConfirmPassword.update(v => !v);
  }

  onSubmit() {
    this.submitted.set(true);

    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    this.authService.setFirstLoginPassword(this.passwordControl.value).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.router.navigate(['/']);
      },
      error: (err) => {
        this.isSubmitting.set(false);

        // 409 = password was already set (e.g. from another tab) — nothing left to do here
        if (err.status === 409) {
          this.authService.markFirstLoginDone();
          this.router.navigate(['/']);
          return;
        }

        const message = err.error?.message;
        this.errorMessage.set(
          (Array.isArray(message) ? message[0] : message) ?? 'Failed to set password. Please try again.'
        );
      },
    });
  }

  onSignOut() {
    // clearSession() (via logout's finalize) navigates to /login
    this.authService.logout().subscribe({ error: () => {} });
  }
}
